import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, addDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { signOut } from 'firebase/auth';
import { auth, db, storage } from '../firebase';
import './ProductManagement.css';

function ProductManagement() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [newProduct, setNewProduct] = useState({
    name: '',
    description: '',
    productLink: ''
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'products'));
      const prods = [];
      querySnapshot.forEach((doc) => {
        prods.push({ id: doc.id, ...doc.data() });
      });
      setProducts(prods);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching products:', error);
      setLoading(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size must be less than 5MB');
        return;
      }

      // Check file type
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }

      setImageFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
  };

  const handleAddProduct = async (e) => {
    e.preventDefault();
    
    if (!newProduct.name.trim() || !newProduct.description.trim()) {
      alert('Please fill in all required fields');
      return;
    }

    setSubmitting(true);

    try {
      let imageUrl = null;

      // Upload image if selected
      if (imageFile) {
        const fileExtension = imageFile.name.split('.').pop();
        const sanitizedProductName = newProduct.name.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
        const fileName = `${sanitizedProductName}_${Date.now()}.${fileExtension}`;
        
        const imageRef = ref(storage, `products/${fileName}`);
        await uploadBytes(imageRef, imageFile);
        imageUrl = await getDownloadURL(imageRef);
      }

      await addDoc(collection(db, 'products'), {
        name: newProduct.name,
        description: newProduct.description,
        imageUrl: imageUrl,
        productLink: newProduct.productLink,
        createdAt: serverTimestamp()
      });

      setNewProduct({ name: '', description: '', productLink: '' });
      setImageFile(null);
      setImagePreview(null);
      setShowForm(false);
      fetchProducts();
      alert('Product added successfully!');
    } catch (error) {
      console.error('Error adding product:', error);
      alert('Error adding product. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteProduct = async (productId, imageUrl) => {
    if (window.confirm('Are you sure you want to delete this product? This will also delete all associated applications.')) {
      try {
        // Delete image from storage if it exists
        if (imageUrl) {
          try {
            const imageRef = ref(storage, imageUrl);
            await deleteObject(imageRef);
          } catch (error) {
            console.log('Error deleting image:', error);
          }
        }

        await deleteDoc(doc(db, 'products', productId));
        fetchProducts();
        alert('Product deleted successfully!');
      } catch (error) {
        console.error('Error deleting product:', error);
        alert('Error deleting product.');
      }
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  if (loading) {
    return (
      <div className="product-management">
        <div className="loading">Loading products...</div>
      </div>
    );
  }

  return (
    <div className="product-management">
      <nav className="product-nav">
        <div className="nav-container">
          <div className="logo">BetaBuyers Admin</div>
          <div className="nav-buttons">
            <button onClick={() => navigate('/admin-dashboard')} className="btn-nav">Dashboard</button>
            <button onClick={handleLogout} className="btn-logout">Sign Out</button>
          </div>
        </div>
      </nav>

      <div className="product-content">
        <div className="product-header">
          <h1>Product Management</h1>
          <button onClick={() => setShowForm(!showForm)} className="btn-add-product">
            {showForm ? '✕ Cancel' : '+ Add New Product'}
          </button>
        </div>

        {showForm && (
          <div className="product-form-container">
            <form onSubmit={handleAddProduct} className="product-form">
              <h2>Create New Product</h2>
              
              <div className="form-group">
                <label>Product Name *</label>
                <input
                  type="text"
                  value={newProduct.name}
                  onChange={(e) => setNewProduct({...newProduct, name: e.target.value})}
                  required
                  disabled={submitting}
                  placeholder="e.g., Smart Watch Pro"
                />
              </div>

              <div className="form-group">
                <label>Description *</label>
                <textarea
                  value={newProduct.description}
                  onChange={(e) => setNewProduct({...newProduct, description: e.target.value})}
                  required
                  disabled={submitting}
                  rows="4"
                  placeholder="Describe the product and what testers will be testing..."
                />
              </div>

              <div className="form-group">
                <label>Product Image (Optional)</label>
                <p className="field-hint">Upload a product image. Max size: 5MB</p>
                
                <div className="image-upload-container">
                  {!imagePreview ? (
                    <label className="image-upload-label">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        className="image-input-hidden"
                        disabled={submitting}
                      />
                      <div className="upload-placeholder">
                        <span className="upload-icon">🖼️</span>
                        <span>Click to upload product image</span>
                      </div>
                    </label>
                  ) : (
                    <div className="image-preview-container">
                      <img src={imagePreview} alt="Preview" className="image-preview" />
                      <button 
                        type="button" 
                        onClick={handleRemoveImage} 
                        className="btn-remove-image"
                        disabled={submitting}
                      >
                        ✕ Remove
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label>Product Link (Optional)</label>
                <input
                  type="url"
                  value={newProduct.productLink}
                  onChange={(e) => setNewProduct({...newProduct, productLink: e.target.value})}
                  disabled={submitting}
                  placeholder="https://example.com/product-page"
                />
                <p className="field-note">Add a link to make the product name clickable (e.g., product website, store page)</p>
              </div>

              <button type="submit" className="btn-submit-product" disabled={submitting}>
                {submitting ? 'Creating Product...' : 'Create Product'}
              </button>
            </form>
          </div>
        )}

        <div className="products-grid">
          {products.length === 0 ? (
            <p className="no-products">No products yet. Create your first product!</p>
          ) : (
            products.map(product => (
              <div key={product.id} className="product-card">
                {product.imageUrl ? (
                  <img src={product.imageUrl} alt={product.name} className="product-image" />
                ) : (
                  <div className="product-no-image">
                    <span className="no-image-icon">📦</span>
                  </div>
                )}
                <div className="product-info">
                  <h3>{product.name}</h3>
                  <p>{product.description}</p>
                  {product.productLink && (
                    <p className="product-link-display">
                      <strong>Link:</strong> <a href={product.productLink} target="_blank" rel="noopener noreferrer">{product.productLink}</a>
                    </p>
                  )}
                  <div className="product-actions">
                    <button 
                      onClick={() => navigate(`/product-applications/${product.id}`)}
                      className="btn-view-apps"
                    >
                      View Applications
                    </button>
                    <button 
                      onClick={() => navigate(`/product-feedback/${product.id}`)}
                      className="btn-view-feedback"
                    >
                      View Feedback
                    </button>
                    <button 
                      onClick={() => handleDeleteProduct(product.id, product.imageUrl)}
                      className="btn-delete"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export default ProductManagement;